import React from 'react';
import { walkClaimsText } from '../../../../lib/walkClaimsText';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import {
  WATER_SCREENING_VIEW_SCHEMA_VERSION,
  type DataSource,
  type SiteRagReport,
  type WaterScreeningView,
} from '../../../../types/siteRag';
import { EditorialCallout, EditorialDataTable, EditorialSubsection } from '../../report/editorial';
import { ReportSourceNote } from '../../report/ReportSourceNote';
import { SectionShell, SectionShellContext, useSectionShellContext } from '../SectionShell';
import { isWaterScreeningViewFaithful } from '../../../../lib/water/waterScreeningViewText';

/** The stored deterministic text, line by line: older reports, and any report whose view no longer matches it. */
const SavedWaterScreeningLines: React.FC<{ report: SiteRagReport }> = ({ report }) => (
  <>
    {report.waterScreening?.split('\n').filter(line => line.trim()).map((line, index) => (
      <p key={index}>{walkClaimsText(line.replace(/^\s*-\s*/, '').trim(), report.references, undefined)}</p>
    ))}
  </>
);

/** Report references the disclosure step added for a water-screen source URL. */
function referenceTokens(url: string, references: readonly DataSource[] | undefined): string {
  return (references ?? [])
    .flatMap((reference, index) => (
      reference.url === url && String(reference.field ?? '').startsWith('economicEvidence.waterUseScreening.')
        ? [`[Ref ${index + 1}]`]
        : []
    ))
    .join(' ');
}

/**
 * A row with no figure of that kind (a benchmark has no 100%-load
 * sensitivity; the adiabatic assist has none either). Never used for a
 * conditional quantity, which is modeled and says so in its own cells.
 */
const NO_FIGURE = '—';
/** The Annual / 100%-load cells of a conditional row: the Average-day cell states the condition. */
const CONDITIONAL_FIGURE = 'conditional';

const WaterScreeningViewBody: React.FC<{ report: SiteRagReport; view: WaterScreeningView }> = ({ report, view }) => {
  const theme = useEffectiveReportTheme();
  const muted = roleColor(theme, 'muted');
  const detailRows = view.rows.filter(row => row.details.length > 0);
  const secondary = (text: string) => (
    <span className="block text-xs font-normal" style={{ color: muted }}>{text}</span>
  );
  // No figure is formatted here: every cell is a string the saved text prints.
  const tableRows: React.ReactNode[][] = view.rows.flatMap(row => {
    const figures: React.ReactNode[] = row.conditional
      ? [
        <span key="average" data-testid="water-screening-conditional">
          {row.conditional.value}
          {secondary(row.conditional.qualifier)}
        </span>,
        <span key="annual" style={{ color: muted }}>{CONDITIONAL_FIGURE}</span>,
        <span key="full" style={{ color: muted }}>{CONDITIONAL_FIGURE}</span>,
      ]
      : [row.averageDay ?? NO_FIGURE, row.annual ?? NO_FIGURE, row.fullLoad ?? NO_FIGURE];
    const scenario: React.ReactNode[] = [
      <span key="scenario" data-testid="water-screening-row-label">
        {row.label}
        {secondary(row.applicability)}
      </span>,
      <span key="basis">{row.boundary}; {row.flowBasis}</span>,
      ...figures,
    ];
    if (!row.assist) return [scenario];
    // The dry path's modeled adiabatic makeup sits directly under its row.
    const assist: React.ReactNode[] = [
      <span key="scenario" data-testid="water-screening-assist-label" style={{ display: 'block', paddingLeft: '1em' }}>
        {row.assist.label}
        {secondary(row.assist.basis)}
      </span>,
      <span key="basis">{row.assist.activeHours}; peak active-hour makeup {row.assist.peakHour}</span>,
      row.assist.averageDay,
      row.assist.annual,
      NO_FIGURE,
    ];
    return [scenario, assist];
  });
  return (
    <div data-testid="water-screening-view" className="space-y-4">
      <EditorialSubsection theme={theme} label="Modeled screening overview" leadCap>
        <p className="m-0">{view.lead}</p>
      </EditorialSubsection>
      <ReportSourceNote variant="label" testId="water-screening-scope">
        Deterministic water-use screen · {view.scope} · modeled screening estimates · benchmark catalog {view.catalogVersion}
      </ReportSourceNote>
      {/* The wide table spans both flow columns and never folds into a <details>. */}
      <div data-span-columns>
        <EditorialDataTable
          theme={theme}
          data-testid="water-screening-table"
          // Every width is explicit: at an equal share the last header broke
          // "SENSITIVITY" mid-word in the A4 PDF. 20% holds that word; the
          // table stays at the printable width (fixed layout).
          columns={[
            { header: 'Modeled scenario', width: '27%' },
            { header: 'Boundary · flow basis', width: '19%' },
            { header: 'Average day', align: 'right', numeric: true, width: '16%' },
            { header: 'Annual', align: 'right', numeric: true, width: '18%' },
            { header: '100%-load cooling sensitivity', align: 'right', numeric: true, width: '20%' },
          ]}
          rows={tableRows}
        />
      </div>
      {detailRows.map(row => (
        <EditorialSubsection key={row.scenarioId} theme={theme} label={`${row.label} · modeled detail`}>
          {row.details.map((detail, index) => <p key={index} className="m-0 mb-2">{detail}</p>)}
        </EditorialSubsection>
      ))}
      <EditorialCallout theme={theme} tone="caveat" label="Key caveats">
        <ul className="m-0 pl-4 list-disc space-y-1">
          {view.caveats.map((caveat, index) => <li key={index}>{caveat}</li>)}
        </ul>
      </EditorialCallout>
      {view.basis.length > 0 && (
        <EditorialSubsection theme={theme} label="Screening inputs">
          {view.basis.map((line, index) => <p key={index} className="m-0 mb-2">{line}</p>)}
          {(() => {
            const looseIntent = report.projectIntentAtGen as (typeof report.projectIntentAtGen & { pue?: number; powerTargetMw?: number }) | undefined;
            const pueVal = looseIntent?.pue ?? looseIntent?.pueValue ?? looseIntent?.pueTarget;
            const powerMw = looseIntent?.powerTargetMw ?? looseIntent?.powerRequirementMW ?? 1000;
            return pueVal != null ? (
              <p className="m-0 mb-2" data-testid="water-screening-base-vs-sensitivity-note">
                Base-case vs. sensitivity note: The PUE 1.15–1.30 band above is the benchmark water-screening sensitivity envelope (yielding 769–870 MW IT load at 1,000 MW facility power), whereas the Project Intent base-case electrical sizing uses PUE {pueVal} ({Math.round((powerMw / pueVal) * 10) / 10} MW IT load) as the conservative planning baseline.
              </p>
            ) : null;
          })()}
        </EditorialSubsection>
      )}
      {view.notes.length > 0 && (
        <EditorialSubsection theme={theme} label="Other modeled assumptions and limitations">
          <ul className="m-0 pl-4 list-disc space-y-1">
            {view.notes.map((note, index) => <li key={index}>{note}</li>)}
          </ul>
        </EditorialSubsection>
      )}
      {view.sources.length > 0 && (
        <EditorialSubsection theme={theme} label="Sources">
          {view.sources.map(source => {
            const tokens = referenceTokens(source.url, report.references);
            return (
              <ReportSourceNote key={source.url} className="m-0 mb-1" testId="water-screening-source">
                {source.citation}
                {' '}
                {tokens ? walkClaimsText(tokens, report.references, undefined) : source.url}
              </ReportSourceNote>
            );
          })}
        </EditorialSubsection>
      )}
    </div>
  );
};

/**
 * This disclosure is deterministic evidence and must never be hidden behind a
 * re-voiced editorial narrative (the line renderer it replaces could not be),
 * so `EditorialSubsection`'s editorial-layer suppression is switched off for
 * the view. Reads the context SectionShell provides and re-provides it.
 */
const NeverEditorialSuppressed: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const shellContext = useSectionShellContext();
  return (
    <SectionShellContext.Provider value={{ ...shellContext, isEditorialActive: false }}>
      {children}
    </SectionShellContext.Provider>
  );
};

/**
 * The view, only while it is a current-schema projection of the saved text. A
 * view whose strings the saved text no longer holds (the text was edited after
 * generation) would show the reader different words than every export, so the
 * saved text is rendered instead.
 */
function currentView(report: SiteRagReport): WaterScreeningView | null {
  const view = report.waterScreeningView;
  return view?.schemaVersion === WATER_SCREENING_VIEW_SCHEMA_VERSION
    && view.rows.length > 0
    && isWaterScreeningViewFaithful(view, report.waterScreening)
    ? view
    : null;
}

/**
 * Render the stored deterministic screen without a fresh calculation or an
 * editorial/model replacement; the reader and exported report share it.
 * A report generated with `waterScreeningView` lays the same formatted strings
 * out as an editorial table; an older report keeps its line-by-line text.
 */
export const WaterScreeningSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const view = currentView(report);
  return (
    <SectionShell label={label} hasData={!!report.waterScreening}>
      {view ? (
        <NeverEditorialSuppressed>
          <WaterScreeningViewBody report={report} view={view} />
        </NeverEditorialSuppressed>
      ) : (
        <SavedWaterScreeningLines report={report} />
      )}
    </SectionShell>
  );
};
